// Image optimization utilities for modern formats and responsive loading
export interface ImageSourceSet {
  avif?: string;
  webp?: string;
  fallback: string;
  sizes?: string;
}

// Standard responsive breakpoints optimized for actual display sizes
const RESPONSIVE_SIZES = [300, 400, 800, 1400] as const;

/**
 * Generate responsive image sources with Supabase transformations
 * Uses Supabase's built-in image transformation API for optimal sizing
 */
export const generateResponsiveImageSources = (
  baseSrc: string,
  breakpoints: readonly number[] = RESPONSIVE_SIZES
): ImageSourceSet => {
  console.log(`[Responsive Sources] Processing: ${baseSrc}`);
  
  if (!baseSrc) {
    console.log(`[Responsive Sources] No baseSrc provided`);
    return { fallback: baseSrc };
  }
  
  // Handle both Supabase storage and local images
  if (!baseSrc.includes('supabase.co/storage')) {
    console.log(`[Responsive Sources] Not a Supabase URL, returning original: ${baseSrc}`);
    return { fallback: baseSrc };
  }
  
  try {
    // Generate srcSet for different sizes using Supabase transforms
    const fallbackSrcSet = breakpoints
      .map(size => `${getSupabaseResizedUrl(baseSrc, size)} ${size}w`)
      .join(', ');
      
    // Only generate WebP if we're confident it works
    const webpSrcSet = breakpoints
      .map(size => `${getSupabaseResizedUrl(baseSrc, size, 'webp')} ${size}w`)
      .join(', ');
    
    console.log(`[Responsive Sources] Generated WebP srcSet: ${webpSrcSet}`);
    console.log(`[Responsive Sources] Generated fallback srcSet: ${fallbackSrcSet}`);
    
    return {
      webp: webpSrcSet,
      fallback: fallbackSrcSet
    };
  } catch (error) {
    console.error(`[Responsive Sources] Error generating sources:`, error);
    console.log(`[Responsive Sources] Falling back to original: ${baseSrc}`);
    return { fallback: baseSrc };
  }
};

/**
 * Get Supabase resized image URL with format conversion
 */
const getSupabaseResizedUrl = (
  originalUrl: string, 
  width: number, 
  format?: 'webp' | 'jpg'
): string => {
  console.log(`[Supabase Transform] Processing URL: ${originalUrl}, Width: ${width}`);
  
  if (!originalUrl.includes('supabase.co/storage')) {
    console.log(`[Supabase Transform] Not a Supabase URL, returning original: ${originalUrl}`);
    return originalUrl;
  }
  
  try {
    // Use Supabase's image transformation API
    const transformUrl = originalUrl.replace('/storage/v1/object/public/', '/storage/v1/render/image/public/');
    const params = new URLSearchParams();
    
    // Set width for responsive sizing
    params.set('width', width.toString());
    
    // Set quality for optimization - higher quality to avoid blurriness
    params.set('quality', '85');
    
    // Use 'contain' resize mode to preserve aspect ratio without cropping
    params.set('resize', 'contain');
    
    // Only add format if explicitly requested and supported
    if (format === 'webp') {
      params.set('format', 'webp');
    }
    
    const finalUrl = `${transformUrl}?${params.toString()}`;
    console.log(`[Supabase Transform] Generated transform URL: ${finalUrl}`);
    return finalUrl;
  } catch (error) {
    console.error(`[Supabase Transform] Error generating URL:`, error);
    console.log(`[Supabase Transform] Falling back to original URL: ${originalUrl}`);
    return originalUrl;
  }
};

/**
 * Test if Supabase Transform API is working for a given URL
 */
const testTransformAPI = async (originalUrl: string): Promise<boolean> => {
  try {
    const testUrl = getSupabaseResizedUrl(originalUrl, 300);
    const response = await fetch(testUrl, { method: 'HEAD' });
    return response.ok;
  } catch {
    return false;
  }
};

// Cache for Transform API availability per domain
const transformAPICache = new Map<string, boolean>();

/**
 * Get optimized image URL with smart fallback system
 * Falls back to original URL if optimization fails
 */
export const getOptimizedImageUrl = (
  originalSrc: string, 
  targetWidth?: number,
  layout: 'hero' | 'card' | 'thumbnail' | 'full-width' | 'custom' = 'card'
): string => {
  if (!originalSrc) {
    console.log(`[Image Optimization] No source provided`);
    return originalSrc;
  }

  // For non-Supabase URLs, return as-is
  if (!originalSrc.includes('supabase.co/storage')) {
    console.log(`[Image Optimization] Not a Supabase URL, returning original: ${originalSrc}`);
    return originalSrc;
  }
  
  // Determine optimal size based on layout
  let optimalWidth = targetWidth;
  if (!optimalWidth) {
    const layoutSizes = {
      hero: 1920,        // Full HD width for hero images
      'full-width': 1920, 
      card: 800,         // Increased from 400 to 800 for sharper images
      thumbnail: 300,
      custom: 400
    };
    optimalWidth = layoutSizes[layout];
  }
  
  console.log(`[Image Optimization] Layout: ${layout}, Target Width: ${optimalWidth}, Original: ${originalSrc}`);
  
  try {
    const optimizedUrl = getSupabaseResizedUrl(originalSrc, optimalWidth);
    
    // Validate that the transform URL was generated correctly
    if (!optimizedUrl.includes('/storage/v1/render/image/')) {
      console.warn(`[Image Optimization] Invalid transform URL generated, using original`);
      return originalSrc;
    }
    
    console.log(`[Image Optimization] Generated optimized URL: ${optimizedUrl}`);
    
    // Return optimized URL - the component will handle fallback if it fails to load
    return optimizedUrl;
  } catch (error) {
    console.warn(`[Image Optimization] Failed to generate optimized URL, using original:`, error);
    return originalSrc;
  }
};

/**
 * Generate sizes attribute for responsive images based on common layouts
 */
export const generateSizesAttribute = (
  layout: 'hero' | 'card' | 'thumbnail' | 'full-width' | 'custom',
  customSizes?: string
): string => {
  if (layout === 'custom' && customSizes) return customSizes;
  
  const sizesMap = {
    hero: '100vw',
    'full-width': '100vw',
    card: '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw',
    thumbnail: '(max-width: 640px) 25vw, 150px'
  };
  
  return sizesMap[layout] || sizesMap.card;
};

/**
 * Calculate optimal image dimensions to prevent layout shift
 */
export const calculateAspectRatio = (
  width: number,
  height: number
): { aspectRatio: string; paddingBottom: string } => {
  const ratio = (height / width) * 100;
  return {
    aspectRatio: `${width}/${height}`,
    paddingBottom: `${ratio}%`
  };
};

/**
 * Preload critical images with modern format support
 */
export const preloadCriticalImage = (src: string, format: 'avif' | 'webp' | 'original' = 'webp') => {
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'image';
  
  if (format === 'avif') {
    link.href = src.replace(/\.[^/.]+$/, '.avif');
    link.type = 'image/avif';
  } else if (format === 'webp') {
    link.href = src.replace(/\.[^/.]+$/, '.webp');
    link.type = 'image/webp';
  } else {
    link.href = src;
  }
  
  document.head.appendChild(link);
  
  // Clean up after a reasonable time
  setTimeout(() => {
    if (link.parentNode) {
      link.parentNode.removeChild(link);
    }
  }, 10000);
};

/**
 * Generate blur-up placeholder data URL for smooth loading
 */
export const generateBlurPlaceholder = (width = 40, height = 30): string => {
  // Clean gradient placeholder instead of checkered pattern
  const svg = `
    <svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style="stop-color:#f9fafb;stop-opacity:1" />
          <stop offset="100%" style="stop-color:#f3f4f6;stop-opacity:1" />
        </linearGradient>
      </defs>
      <rect width="100%" height="100%" fill="url(#grad)"/>
    </svg>
  `;
  
  return `data:image/svg+xml;base64,${btoa(svg)}`;
};